import React from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import {
    useCancelBuildMutation,
    useDeleteBuildMutation,
    useFetchProductByIdQuery,
    useUpdateBuildChannelMutation,
} from "@dashboard/products/api";
import { LogStreamer } from "@dashboard/products/components/LogStreamer/LogStreamer";
import { Build } from "@dashboard/products/types";
import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { createBuildVariantTag, createRolloutTag } from "@dashboard/shared/tags";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { DialogContentText, Typography, useTheme } from "@mui/material";
import CompareService from "@services/compareService/compareService";
import TimeService from "@services/timeService/timeService";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { DownloadActionsMenu } from "./DownloadActionsMenu/DownloadActionsMenu";
import { BuildSettingsDialog } from "./SettingsDialog/BuildSettingsDialog";
import { Artifacts } from "./Artifacts";
import { useStyles } from "./Cells.style";
import { isBuildProcessing } from "./isBuildProcessing";

type ChangesTooltipProps = {
    kind: string;
    changes: string[];
};
const ChangesTooltip = (props: ChangesTooltipProps) => {
    const { t } = useTranslation(["pages"]);
    const theme = useTheme();
    let title = "-";

    switch (props.kind) {
        case "ADD":
            title = t("productPage.buildsTable.buildDiffTooltip.add");
            break;
        case "REMOVE":
            title = t("productPage.buildsTable.buildDiffTooltip.remove");
            break;
        case "REPLACE":
        case "UPDATE":
            title = t("productPage.buildsTable.buildDiffTooltip.update");
            break;
    }

    return (
        <React.Fragment>
            <Typography variant="subtitle2">{title}:</Typography>
            <ul style={{ paddingLeft: theme.spacing(4) }}>
                {props.changes.map((value) => (
                    <li key={value}>
                        <Typography variant="caption">{value}</Typography>
                    </li>
                ))}
            </ul>
        </React.Fragment>
    );
};

type StatusCellProps = {
    buildStatus: API.Build["buildStatus"] | null;
    build: Build;
    productId: number;
};

export const StatusCell = (props: StatusCellProps) => {
    const theme = useTheme();
    const { classes, cx } = useStyles();

    const { data: product } = useFetchProductByIdQuery(props.productId);

    const compare = new CompareService(product?.customBuildConfig, props.build?.configSnapshot);
    const differencesReport = compare.getDifferencesReport();

    const getStatusIcon = () => {
        switch (props.buildStatus) {
            case "running":
                return (
                    <Tooltip title="Build is running" placement="right">
                        <i className="fas fa-spinner fa-spin" style={{ color: theme.palette.emteria.primary }} />
                    </Tooltip>
                );
            case "pending":
                return (
                    <Tooltip title="Build is pending" placement="right">
                        <i className="fas fa-hourglass-half" style={{ color: theme.palette.emteria.text.secondary }} />
                    </Tooltip>
                );
            case "canceled":
                return (
                    <Tooltip title="Build canceled" placement="right">
                        <i className="fas fa-ban" style={{ color: theme.palette.emteria.text.secondary }} />
                    </Tooltip>
                );
            case "skipped":
                return (
                    <Tooltip title="Build skipped" placement="right">
                        <i className="fa-regular fa-circle-right" style={{ color: theme.palette.emteria.text.secondary }} />
                    </Tooltip>
                );
            case "succeeded":
                return (
                    <Tooltip title="Build succeeded" placement="right">
                        <i className="fa-regular fa-circle-check" style={{ color: theme.palette.emteria.success }} />
                    </Tooltip>
                );
            case "failed":
                return (
                    <Tooltip title="Build failed" placement="right">
                        <i className="fa-regular fa-circle-xmark" style={{ color: theme.palette.emteria.error }} />
                    </Tooltip>
                );
            default:
                return (
                    <Tooltip title="Unknown status" placement="right">
                        <i className="fa-regular fa-circle-question" style={{ color: theme.palette.emteria.error }} />
                    </Tooltip>
                );
        }
    };

    return (
        <MinimalTableCell width={40}>
            {getStatusIcon()}
            {compare.hasDifferences ? (
                <Tooltip
                    placement="right"
                    title={Object.entries(differencesReport).map(([kind, changes]) => (
                        <ChangesTooltip key={kind} {...{ kind, changes }} />
                    ))}
                >
                    <i className={cx(classes.oldConfigIcon, "fa fa-info-circle")} />
                </Tooltip>
            ) : null}
        </MinimalTableCell>
    );
};

type TimestampCellProps = {
    created: string | null;
    finished: string | null;
};
export const TimestampCell = (props: TimestampCellProps) => {
    const { t } = useTranslation(["pages"]);

    if (!props.created) {
        return (
            <MinimalTableCell width={100}>
                <Typography>-</Typography>
            </MinimalTableCell>
        );
    }

    const displayDate = TimeService.getLocalDate(props.created, "ISO", "date");
    const createdDateTime = TimeService.getLocalDate(props.created, "ISO", "dateTime");
    const finishedDateTime = props.finished ? TimeService.getLocalDate(props.finished, "ISO", "dateTime") : null;

    const createdLabel = t("pages:productPage.buildsTable.tooltip.created", { value: createdDateTime });
    const finishedLabel = finishedDateTime
        ? t("pages:productPage.buildsTable.tooltip.finished", { value: finishedDateTime })
        : null;

    const tooltipContent = (
        <React.Fragment>
            <Typography variant="body2">{createdLabel}</Typography>
            {finishedLabel ? <Typography variant="body2">{finishedLabel}</Typography> : null}
        </React.Fragment>
    );

    return (
        <MinimalTableCell width={100}>
            <Tooltip title={tooltipContent}>
                <Typography>{displayDate}</Typography>
            </Tooltip>
        </MinimalTableCell>
    );
};

type ArtifactsCellProps = {
    artifacts: Build["artifacts"];
};
export const ArtifactsCell = (props: ArtifactsCellProps) => {
    const theme = useTheme();
    const { t } = useTranslation(["pages"]);

    const hasArtifacts = props.artifacts?.length > 0;
    const uploadedArtifacts = props.artifacts?.filter((item) => item.isUploaded) ?? [];
    const uploadingArtifacts = props.artifacts?.filter((item) => !item.isUploaded) ?? [];

    if (!hasArtifacts) {
        return (
            <MinimalTableCell>
                <Typography>-</Typography>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell>
            <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                {uploadedArtifacts.length ? <Artifacts artifacts={uploadedArtifacts} /> : null}
                {uploadingArtifacts.length ? (
                    <Tooltip title={t("pages:productPage.buildsTable.artifactsUploading")} placement="top">
                        <i className="fas fa-spinner fa-spin" style={{ color: theme.palette.emteria.primary }} />
                    </Tooltip>
                ) : null}
            </div>
        </MinimalTableCell>
    );
};

type TagsCellProps = {
    build: Build;
};
export const TagsCell = (props: TagsCellProps) => {
    const { classes } = useStyles();
    const removableTags = props.build.metadata?.user?.tags;
    const rolloutTag = createRolloutTag(props.build.rolloutPercentage);
    const buildVariantTag = createBuildVariantTag(props.build);
    const hasTags = removableTags?.length || rolloutTag || buildVariantTag;

    return (
        <MinimalTableCell className={classes.tagsBox}>
            {removableTags?.length ? removableTags.map((item) => <Tag key={item.key} item={item} />) : null}
            {rolloutTag ? <Tag key={rolloutTag.key} item={rolloutTag} theme="light" /> : null}
            {buildVariantTag ? <Tag key={buildVariantTag.key} item={buildVariantTag} theme="light" /> : null}
            {!hasTags ? <Typography>-</Typography> : null}
        </MinimalTableCell>
    );
};

const channelOptions = [
    { label: "Live", value: "live" },
    { label: "Beta", value: "beta" },
    { label: "Nightly", value: "nightly" },
    { label: "None", value: "none" },
];
type ChannelCellProps = {
    build: Build;
};
export const ChannelCell = (props: ChannelCellProps) => {
    const { classes } = useStyles();
    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const canceledOrFailedBuild = props.build.buildStatus === "canceled" || props.build.buildStatus === "failed";

    const [updateBuildChannel] = useUpdateBuildChannelMutation();

    const onChannelChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string") {
            return;
        }
        const selectedChannel = selectedOption.value as Build["channel"];

        const result = await updateBuildChannel({
            productId: props.build.productId,
            buildId: props.build.buildId,
            channel: selectedChannel,
        });
        if (!("error" in result)) {
            showSuccessToast("The build channel was successfully changed");
        }
    };
    const getChannelInputTooltip = () => {
        if (cannotManageProducts) {
            return "Your role does not allow you to edit builds";
        } else if (canceledOrFailedBuild) {
            return "Unfinished builds cannot be released";
        } else {
            return "";
        }
    };

    return (
        <MinimalTableCell>
            <Tooltip title={getChannelInputTooltip()}>
                <SelectField
                    className={classes.channelSelect}
                    value={channelOptions.find((option) => option.value === props.build.channel)}
                    options={channelOptions}
                    onChange={onChannelChange}
                    disabled={canceledOrFailedBuild || cannotManageProducts}
                    disableSorting
                />
            </Tooltip>
        </MinimalTableCell>
    );
};

type BuildActionsCellProps = {
    build: Build;
    productId: number;
};
export const BuildActionsCell = (props: BuildActionsCellProps) => {
    const [openSettingsDialog, setOpenSettingsDialog] = React.useState(false);
    const [isCanceling, setIsCanceling] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isDeleteBuildDialogOpen, setIsDeleteBuildDialogOpen] = React.useState(false);
    const { classes } = useStyles();

    const [cancelBuild] = useCancelBuildMutation();
    const [deleteBuild] = useDeleteBuildMutation();

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const buildProcessing = isBuildProcessing(props.build);

    const handleCancel = async () => {
        setIsCanceling(true);
        const result = await cancelBuild(props.build.buildId);
        if (!("error" in result)) {
            showSuccessToast("Build canceled successfully");
        }
        setIsCanceling(false);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        const result = await deleteBuild({ productId: props.build.productId, buildId: props.build.buildId });
        if (!("error" in result)) {
            showSuccessToast("Build deleted successfully");
        }
        setIsDeleting(false);
        setIsDeleteBuildDialogOpen(false);
    };

    return (
        <MinimalTableCell width={120} style={{ overflow: "visible", position: "relative" }}>
            <div className={classes.actionBox}>
                <DownloadActionsMenu build={props.build} />
                <SecondaryButton
                    onClick={() => setOpenSettingsDialog(true)}
                    tooltipProps={{
                        title: cannotManageProducts ? "Your role does not allow you to edit builds" : "Edit build settings",
                    }}
                    disabled={cannotManageProducts}
                >
                    <i className="fa-solid fa-gear" />
                </SecondaryButton>
                <LogStreamer buildId={props.build.buildId} hasLogs={props.build.hasLogs} disabled={false} />
                {buildProcessing ? (
                    <DangerOutlinedButton
                        onClick={handleCancel}
                        tooltipProps={{
                            title: cannotManageProducts ? "Your role does not allow you to cancel builds" : "Cancel build",
                        }}
                        disabled={isCanceling || cannotManageProducts}
                        loading={isCanceling}
                    >
                        <i className="fa-solid fa-ban" />
                    </DangerOutlinedButton>
                ) : (
                    <DangerOutlinedButton
                        onClick={() => setIsDeleteBuildDialogOpen(true)}
                        tooltipProps={{
                            title: cannotManageProducts ? "Your role does not allow you to delete build" : "Delete build",
                        }}
                        disabled={isDeleting || cannotManageProducts}
                        loading={isDeleting}
                    >
                        <i className="fas fa-trash-alt" />
                    </DangerOutlinedButton>
                )}
            </div>
            {openSettingsDialog ? (
                <BuildSettingsDialog open={openSettingsDialog} build={props.build} onClose={() => setOpenSettingsDialog(false)} />
            ) : null}
            <ConfirmDialog
                title="Delete build"
                open={isDeleteBuildDialogOpen}
                onConfirm={handleDelete}
                onClose={() => setIsDeleteBuildDialogOpen(false)}
                dangerButton
                primaryActionText="Delete"
            >
                <DialogContentText>{`Are you sure you want to delete version ${props.build.version}?`}</DialogContentText>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};
